import { TaskTypeEnum } from '@api';
import { FormStepModel } from '@components/form';
import { useApi, createQuestionnaireAnswer } from '@data-access';
import { ErrorHelper } from '@helpers';
import { hasStyleMetric, STYLE_TASK_TYPES } from '@lib/styleMetric';
import { TextGenerationStep, TabularMLStep1, TabularMLStep2, StyleStep } from '@sections/data-collection';
import { DataCollectionStepId, validateDataCollectionStep } from '@sections/data-collection/dataCollectionValidation';
import { questionnaireStore } from '@stores';
import { DataCollectionInputs, DataCollectionOptions } from '@types';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { handleAssessment } from 'src/data-access/handle-assessment';

import { DataCollectionShownStep } from './useDataCollectionNavigation';

interface UseDataCollectionQuestionnaireParams {
  options: DataCollectionOptions | undefined;
  setShownStep: (step: DataCollectionShownStep) => void;
  setCompleted: (completed: boolean) => void;
}

export const useDataCollectionQuestionnaire = ({
  options,
  setShownStep,
  setCompleted,
}: UseDataCollectionQuestionnaireParams) => {
  const { api, questionnaireApi } = useApi();
  const { push } = useRouter();

  const {
    currentStep,
    maxStep,
    selectedRiskMetrics,
    questionnaireData,
    latestAssessment,
    addQuestionnaireData,
    setCurrentStep,
    setMaxStep,
    setQuestionnaireData,
    setSelectedQuestionnaireAnswersOption,
  } = questionnaireStore;

  const [submitting, setSubmitting] = useState(false);
  const [isLongAssessment, setIsLongAssessment] = useState(false);
  const submitLockRef = useRef(false);

  const formMethods = useForm<DataCollectionInputs>({
    mode: 'onChange',
    defaultValues: {
      modelType: questionnaireData?.modelType || null,
      performanceMetricRanked: questionnaireData?.performanceMetricRanked || [],
      protectedGroupsRanked: questionnaireData?.protectedGroupsRanked || [],
      protectedGroupsFile: questionnaireData?.protectedGroupsFile || null,
      reportType: questionnaireData?.reportType || null,
      textGenerationPersonalDataCategories: questionnaireData?.textGenerationPersonalDataCategories || [],
      textGenerationInsightfulnessCriteria: questionnaireData?.textGenerationInsightfulnessCriteria || [],
      styleCriteria: questionnaireData?.styleCriteria || [],
    } as Partial<DataCollectionInputs>,
  });

  const { reset } = formMethods;

  useEffect(() => {
    reset({
      modelType: questionnaireData?.modelType || null,
      performanceMetricRanked: questionnaireData?.performanceMetricRanked || [],
      protectedGroupsRanked: questionnaireData?.protectedGroupsRanked || [],
      protectedGroupsFile: questionnaireData?.protectedGroupsFile || null,
      reportType: questionnaireData?.reportType || null,
      textGenerationPersonalDataCategories: questionnaireData?.textGenerationPersonalDataCategories || [],
      textGenerationInsightfulnessCriteria: questionnaireData?.textGenerationInsightfulnessCriteria || [],
      styleCriteria: questionnaireData?.styleCriteria || [],
    } as Partial<DataCollectionInputs>);
  }, [questionnaireData, reset]);

  const getDistinctIndicators = useCallback(
    () => [...new Set(selectedRiskMetrics.map(metric => metric.indicator.name))],
    [selectedRiskMetrics],
  );

  const submitData = useCallback(
    async (data: DataCollectionInputs) => {
      if (submitLockRef.current) return;
      submitLockRef.current = true;
      setSubmitting(true);
      try {
        const questionnaireAnswerId = await createQuestionnaireAnswer(questionnaireApi, data, selectedRiskMetrics);
        setSelectedQuestionnaireAnswersOption(null);
        await handleAssessment(
          questionnaireAnswerId,
          api,
          push,
          setCompleted,
          setIsLongAssessment,
          setSubmitting,
          () => {
            submitLockRef.current = false;
          },
        );
      } catch (error) {
        ErrorHelper.handleErrorNotification(error);
        setSubmitting(false);
        submitLockRef.current = false;
      }
    },
    [api, questionnaireApi, push, selectedRiskMetrics, setCompleted, setSelectedQuestionnaireAnswersOption],
  );

  const stepIds = useMemo<DataCollectionStepId[]>(() => {
    const taskType = questionnaireData?.taskType;
    const selectedIndicators = getDistinctIndicators();
    const steps: DataCollectionStepId[] = [];

    switch (taskType) {
      case TaskTypeEnum.tabularMl:
        if (selectedIndicators.some(name => name === 'performance' || name === 'robustness')) {
          steps.push('tabularMlStep1');
        }
        if (selectedIndicators.some(name => name === 'fairness')) {
          steps.push('tabularMlStep2');
        }
        break;
      case TaskTypeEnum.textGeneration:
        if (
          selectedRiskMetrics.some(metric =>
            ['Personal Data Exposure', 'Criteria Insightfulness', 'Key Takeaway Score'].includes(metric.name),
          )
        ) {
          steps.push('textGenerationStep');
        }
        break;
      default:
        break;
    }

    // Style gets its own step for every task type that has the metric (single turn chat, text generation,
    // conversational agent, voice agent). With no steps at all, the questionnaire submits directly.
    if (STYLE_TASK_TYPES.includes(taskType) && hasStyleMetric(selectedRiskMetrics)) {
      steps.push('styleStep');
    }

    return steps;
  }, [getDistinctIndicators, questionnaireData?.taskType, selectedRiskMetrics]);

  const totalSteps = stepIds.length;

  const steps = useMemo<FormStepModel[]>(() => {
    const stepsArray: FormStepModel[] = [];
    let stepNumber = 1;

    stepIds.forEach(stepId => {
      switch (stepId) {
        case 'tabularMlStep1':
          stepsArray.push({
            title: 'Model type and metrics',
            content: <TabularMLStep1 options={options} />,
            step: stepNumber,
          });
          stepNumber += 1;
          break;
        case 'tabularMlStep2':
          stepsArray.push({
            title: 'Now we dive deeper into the model to learn more about the model behaviour',
            content: <TabularMLStep2 options={options} />,
            step: stepNumber,
          });
          stepNumber += 1;
          break;
        case 'textGenerationStep':
          stepsArray.push({
            title: 'Text generation',
            content: <TextGenerationStep />,
            step: stepNumber,
          });
          stepNumber += 1;
          break;
        case 'styleStep':
          stepsArray.push({
            title: 'Style criteria',
            content: <StyleStep />,
            step: stepNumber,
          });
          stepNumber += 1;
          break;
        default:
          break;
      }
    });

    return stepsArray;
  }, [options, stepIds]);

  const handleStepSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitLockRef.current) return;
      const submitter = (event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
      const buttonId = submitter?.id;
      const stepId = stepIds[currentStep - 1];
      const formValues = formMethods.getValues();

      if (!stepId) return;

      if (buttonId === 'back') {
        addQuestionnaireData(formValues);
        if (currentStep > 1) {
          setCurrentStep(currentStep - 1);
        } else {
          setShownStep('testing');
        }
        return;
      }

      try {
        await validateDataCollectionStep(formValues, {
          stepId,
          taskType: questionnaireData?.taskType,
          selectedRiskMetrics,
        });

        addQuestionnaireData(formValues);

        if (currentStep === totalSteps) {
          await submitData({ ...questionnaireData, ...formValues });
          return;
        }

        setCurrentStep(currentStep + 1);
        if (maxStep < currentStep + 1) {
          setMaxStep(currentStep + 1);
        }
      } catch (error) {
        error.inner?.forEach((inner, index) => {
          const { type, path, errors: formErrors } = inner;
          if (index === 0 && path) {
            formMethods.setFocus(path as any);
          }
          if (path) {
            formMethods.setError(path as any, { type, message: formErrors[0] });
          }
        });
      }
    },
    [
      addQuestionnaireData,
      currentStep,
      formMethods,
      maxStep,
      questionnaireData,
      selectedRiskMetrics,
      setCurrentStep,
      setMaxStep,
      setShownStep,
      stepIds,
      submitData,
      totalSteps,
    ],
  );

  const stepsWithHandlers = useMemo(
    () =>
      steps.map(step => ({
        ...step,
        content: (
          <form
            id={`form-id-${step.step}`}
            onSubmit={handleStepSubmit}
          >
            {step.content}
          </form>
        ),
      })),
    [handleStepSubmit, steps],
  );

  return {
    formMethods,
    submitting,
    isLongAssessment,
    stepsWithHandlers,
    submitData,
  };
};
